import { useEffect, useRef, useState } from "react";
import { Link, useLocation, matchPath } from "react-router-dom";
import ChangePasswordModal from "../ChangePasswordModal";
import { confirmAction } from "../../utils/confirm";
import { useAuthController } from "../../controllers/useAuthController";

const titles = [
  { pattern: "/dashboard", title: "Dashboard" },
  { pattern: "/registrations", title: "Families" },
  { pattern: "/families/:id/edit", title: "Edit Family" },
  { pattern: "/families", title: "Families" },
  { pattern: "/children", title: "Families" },
  { pattern: "/submissions", title: "Form Submissions" },
  { pattern: "/forms", title: "Forms Catalog" },
  { pattern: "/documents", title: "Documents" },
  { pattern: "/policies", title: "Policies" },
  { pattern: "/handbook", title: "Handbook" },
  { pattern: "/centres", title: "Centres" },
  { pattern: "/users", title: "Admin Users" },
  { pattern: "/roles", title: "Roles" },
  { pattern: "/roles/:id/permissions", title: "Role Permissions" },
  { pattern: "/activity", title: "Activity Log" },
  { pattern: "/settings", title: "Settings" },
];

const toggleSidebar = () => {
  const el = document.querySelector("#sidebar");
  if (window.coreui && el) {
    window.coreui.Sidebar.getInstance(el)?.toggle();
  }
};

export default function Header() {
  const { pathname } = useLocation();
  const { user, handleLogout } = useAuthController();

  const matched = titles.find((t) =>
    matchPath({ path: t.pattern, end: true }, pathname)
  );
  const title = matched?.title || "Admin";

  const displayName = user?.full_name || user?.name || user?.email || "Admin";
  const roleLabel =
    (user?.role_name || "").toLowerCase() === "group_admin"
      ? "Group Admin"
      : "Centre Admin";

  const [open, setOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const onLogoutClick = async () => {
    setOpen(false);
    const result = await confirmAction({
      title: "Are you sure?",
      text: "You want to log out.",
      icon: "warning",
      confirmText: "Logout",
      variant: "danger",
    });
    if (result.isConfirmed) handleLogout();
  };

  return (
    <header className="header header-sticky p-0 mb-4">
      <div className="container-fluid px-4">
        <button
          className="header-toggler"
          type="button"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar"
        >
          <i className="cil-menu" />
        </button>

        <ul className="header-nav">
          {user?.centre_name && (
            <li className="nav-item d-none d-md-flex align-items-center me-2">
              <span className="centre-badge">
                <i className="cil-location-pin me-1" />
                {user.centre_name}
              </span>
            </li>
          )}
          <li className="nav-item">
            <div className="app-dropdown" ref={ref}>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={open}
              >
                <span className="d-none d-md-inline text-body fw-semibold">
                  {displayName}
                </span>
                <span className="avatar-img d-flex align-items-center justify-content-center text-white fw-bold rounded-circle">
                  {String(displayName).charAt(0).toUpperCase()}
                </span>
              </button>

              {open && (
                <div className="app-dropdown-menu app-dropdown-menu-end pt-0">
                  <div className="dropdown-header px-3 py-2 fw-semibold text-body-secondary">
                    {roleLabel}
                  </div>
                  <button
                    type="button"
                    className="dropdown-item"
                    onClick={() => {
                      setOpen(false);
                      setPasswordOpen(true);
                    }}
                  >
                    <i className="cil-lock-locked me-2" />
                    Change password
                  </button>
                  <div className="dropdown-divider" />
                  <button
                    type="button"
                    className="dropdown-item text-danger"
                    onClick={onLogoutClick}
                  >
                    <i className="cil-account-logout me-2" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </li>
        </ul>
      </div>

      <div className="container-fluid px-4">
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb my-0">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Admin</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {title}
            </li>
          </ol>
        </nav>
      </div>

      <ChangePasswordModal open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </header>
  );
}
